import { useState, useCallback, useMemo, useEffect } from "react";
import { ArrowLeft, ArrowRight, Check, Loader2, Sparkles, X, CalendarClock, Gauge } from "lucide-react";
import { toast } from "sonner";
import { saveStoryStudio, createOrder, verifyPayment, loadRazorpayScript, getDailyLimit } from "../lib/api";
import { usePricing, formatINR } from "../lib/pricing";
import { LaunchPill } from "./PricingBadge";
import { ConsentBlock } from "./ConsentBlock";
import { LegalFooter } from "./LegalFooter";

const HOW_MET_OPTIONS = [
    "School", "College", "Workplace", "Friends introduced us",
    "Family introduction", "Dating app", "Social media", "Other",
];

const VIBE_WORDS = [
    "Romantic", "Playful", "Calm", "Elegant", "Emotional", "Adventurous",
    "Traditional", "Modern", "Luxury", "Funny", "Creative", "Free-spirited",
];

const COUPLE_ENERGY = [
    "Quiet & intimate", "Cheerful & playful", "Cinematic & dramatic",
    "Sophisticated & elegant", "Natural & candid",
];

const STYLE_DIRECTION = [
    "Romantic", "Luxury", "Royal", "Editorial", "Cinematic", "Minimal",
    "Vintage", "Traditional Tamil", "South Indian Temple", "Kerala Heritage",
    "Beach", "Garden", "Palace", "Moody", "Adventure", "Urban", "Fine Art",
];

const VISUAL_STYLE = [
    "Vogue Editorial", "Pinterest Luxury", "Movie Poster",
    "Netflix Romance", "Documentary", "Fine Art Wedding",
];

const HOBBIES = [
    "Coffee", "Food", "Travel", "Reading", "Music", "Cricket", "Gym",
    "Pets", "Nature", "Bikes", "Movies", "Dancing", "Photography", "Other",
];

const SHOOT_LOCATIONS = [
    "Beach", "Heritage streets", "Temple", "Palace", "Mountains", "Forest",
    "Waterfalls", "Coffee shop", "Studio", "Urban city", "Rooftop", "Resort", "Other",
];

const SHOOT_TIMES = ["Sunrise", "Golden Hour", "Sunset", "Blue Hour", "Night"];

const PHOTO_STYLES = ["Editorial", "Documentary", "Fine Art", "Luxury", "Cinematic"];

const PROPS = [
    "Pet", "Bike", "Car", "Scooter", "Musical instrument", "Books",
    "Flowers", "Traditional family heirloom", "Sports equipment", "Other props",
];

const STEPS = [
    { key: "love_story", title: "Your Love Story" },
    { key: "personality", title: "Personality" },
    { key: "style", title: "Style Direction" },
    { key: "lifestyle", title: "Lifestyle" },
    { key: "shoot", title: "Dream Shoot" },
    { key: "touches", title: "Personal Touches" },
];

const TOTAL = STEPS.length;
// Razorpay payment link removed. New payment gateway will be wired in later.

// --------------------------- Small UI primitives ---------------------------
function Chip({ active, onClick, children, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`rounded-full border px-3.5 py-2 text-[12.5px] transition ${
                active
                    ? "border-charcoal bg-charcoal text-ivory"
                    : "border-charcoal/15 bg-white text-charcoal hover:border-charcoal/40"
            }`}
        >
            {children}
        </button>
    );
}

function FieldLabel({ children, sub }) {
    return (
        <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-charcoal/55">Question</div>
            <h3 className="mt-2 font-serif text-2xl leading-tight text-charcoal md:text-[28px]">{children}</h3>
            {sub && <p className="mt-2 text-[13.5px] text-ink">{sub}</p>}
        </div>
    );
}

function Textarea({ value, onChange, placeholder, rows = 4, testId }) {
    return (
        <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={rows}
            data-testid={testId}
            className="mt-4 w-full rounded-[18px] border border-charcoal/15 bg-white p-4 text-[14.5px] leading-relaxed text-charcoal placeholder:text-charcoal/35 focus:border-charcoal focus:outline-none"
        />
    );
}

function TextInput({ value, onChange, placeholder, testId }) {
    return (
        <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            data-testid={testId}
            className="mt-4 w-full rounded-full border border-charcoal/15 bg-white px-5 py-3 text-[14px] text-charcoal placeholder:text-charcoal/35 focus:border-charcoal focus:outline-none"
        />
    );
}

// --------------------------- Step renderers ---------------------------
function StepLoveStory({ form, set }) {
    return (
        <div className="space-y-10" data-testid="story-step-love-story">
            <div>
                <FieldLabel>How did you meet?</FieldLabel>
                <div className="mt-4 flex flex-wrap gap-2">
                    {HOW_MET_OPTIONS.map((o) => (
                        <Chip key={o} active={form.how_met === o} onClick={() => set({ how_met: o })} testId={`how-met-${o.toLowerCase().replace(/\s+/g, "-")}`}>
                            {o}
                        </Chip>
                    ))}
                </div>
                {form.how_met === "Other" && (
                    <TextInput value={form.how_met_other} onChange={(v) => set({ how_met_other: v })} placeholder="Tell us how" testId="how-met-other-input" />
                )}
            </div>

            <div>
                <FieldLabel sub="Describe your journey from the day you met until today.">Tell us your story</FieldLabel>
                <Textarea value={form.story} onChange={(v) => set({ story: v })} placeholder="Take your time — the more you share, the richer your concept will be." rows={6} testId="story-text" />
            </div>

            <div>
                <FieldLabel sub="First date · first trip · proposal · funny incident · long-distance reunion · family acceptance">Most memorable moment together</FieldLabel>
                <Textarea value={form.memorable_moment} onChange={(v) => set({ memorable_moment: v })} placeholder="What moment do you keep coming back to?" testId="memorable-moment" />
            </div>

            <div className="grid gap-6 md:grid-cols-2">
                <div>
                    <FieldLabel sub="Bride's words">What do you love most about your partner?</FieldLabel>
                    <Textarea value={form.bride_loves} onChange={(v) => set({ bride_loves: v })} placeholder="In a few honest sentences…" testId="bride-loves" />
                </div>
                <div>
                    <FieldLabel sub="Groom's words">What do you love most about your partner?</FieldLabel>
                    <Textarea value={form.groom_loves} onChange={(v) => set({ groom_loves: v })} placeholder="In a few honest sentences…" testId="groom-loves" />
                </div>
            </div>
        </div>
    );
}

function StepPersonality({ form, set }) {
    function toggleVibe(w) {
        const cur = form.vibe_words || [];
        if (cur.includes(w)) {
            set({ vibe_words: cur.filter((x) => x !== w) });
        } else if (cur.length < 3) {
            set({ vibe_words: [...cur, w] });
        } else {
            toast.error("Pick up to 3.");
        }
    }
    return (
        <div className="space-y-10" data-testid="story-step-personality">
            <div>
                <FieldLabel sub="Pick up to 3 that describe your relationship.">Three words for your bond</FieldLabel>
                <div className="mt-4 flex flex-wrap gap-2">
                    {VIBE_WORDS.map((w) => (
                        <Chip key={w} active={(form.vibe_words || []).includes(w)} onClick={() => toggleVibe(w)} testId={`vibe-${w.toLowerCase()}`}>
                            {w}
                        </Chip>
                    ))}
                </div>
            </div>
            <div>
                <FieldLabel>Your energy as a couple</FieldLabel>
                <div className="mt-4 flex flex-wrap gap-2">
                    {COUPLE_ENERGY.map((e) => (
                        <Chip key={e} active={form.couple_energy === e} onClick={() => set({ couple_energy: e })} testId={`energy-${e.split(" ")[0].toLowerCase()}`}>
                            {e}
                        </Chip>
                    ))}
                </div>
            </div>
        </div>
    );
}

function StepStyle({ form, set }) {
    function toggleStyle(w) {
        const cur = form.style_direction || [];
        set({ style_direction: cur.includes(w) ? cur.filter((x) => x !== w) : [...cur, w] });
    }
    return (
        <div className="space-y-10" data-testid="story-step-style">
            <div>
                <FieldLabel sub="Choose all that apply — we'll weight them by frequency.">Aesthetic direction</FieldLabel>
                <div className="mt-4 flex flex-wrap gap-2">
                    {STYLE_DIRECTION.map((s) => (
                        <Chip key={s} active={(form.style_direction || []).includes(s)} onClick={() => toggleStyle(s)} testId={`style-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                            {s}
                        </Chip>
                    ))}
                </div>
            </div>
            <div>
                <FieldLabel>Which visual world do you admire most?</FieldLabel>
                <div className="mt-4 flex flex-wrap gap-2">
                    {VISUAL_STYLE.map((s) => (
                        <Chip key={s} active={form.visual_style === s} onClick={() => set({ visual_style: s })} testId={`visual-${s.split(" ")[0].toLowerCase()}`}>
                            {s}
                        </Chip>
                    ))}
                </div>
            </div>
        </div>
    );
}

function StepLifestyle({ form, set }) {
    function toggleHobby(w) {
        const cur = form.hobbies || [];
        set({ hobbies: cur.includes(w) ? cur.filter((x) => x !== w) : [...cur, w] });
    }
    return (
        <div className="space-y-8" data-testid="story-step-lifestyle">
            <div className="grid gap-5 md:grid-cols-2">
                <div>
                    <FieldLabel>Favorite travel destination</FieldLabel>
                    <TextInput value={form.favorite_travel} onChange={(v) => set({ favorite_travel: v })} placeholder="e.g. Goa" testId="fav-travel" />
                </div>
                <div>
                    <FieldLabel>Favorite movie together</FieldLabel>
                    <TextInput value={form.favorite_movie} onChange={(v) => set({ favorite_movie: v })} placeholder="e.g. 96" testId="fav-movie" />
                </div>
                <div>
                    <FieldLabel>Favorite song</FieldLabel>
                    <TextInput value={form.favorite_song} onChange={(v) => set({ favorite_song: v })} placeholder="One song that's yours" testId="fav-song" />
                </div>
                <div>
                    <FieldLabel>Dream honeymoon destination</FieldLabel>
                    <TextInput value={form.dream_honeymoon} onChange={(v) => set({ dream_honeymoon: v })} placeholder="e.g. Santorini" testId="dream-honeymoon" />
                </div>
                <div className="md:col-span-2">
                    <FieldLabel>Favorite café or hangout</FieldLabel>
                    <TextInput value={form.favorite_hangout} onChange={(v) => set({ favorite_hangout: v })} placeholder="Your spot" testId="fav-hangout" />
                </div>
            </div>
            <div>
                <FieldLabel>Shared hobbies</FieldLabel>
                <div className="mt-4 flex flex-wrap gap-2">
                    {HOBBIES.map((h) => (
                        <Chip key={h} active={(form.hobbies || []).includes(h)} onClick={() => toggleHobby(h)} testId={`hobby-${h.toLowerCase()}`}>
                            {h}
                        </Chip>
                    ))}
                </div>
            </div>
        </div>
    );
}

function StepShoot({ form, set }) {
    return (
        <div className="space-y-10" data-testid="story-step-shoot">
            <div>
                <FieldLabel>Preferred shoot location</FieldLabel>
                <div className="mt-4 flex flex-wrap gap-2">
                    {SHOOT_LOCATIONS.map((s) => (
                        <Chip key={s} active={form.shoot_location === s} onClick={() => set({ shoot_location: s })} testId={`shoot-loc-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                            {s}
                        </Chip>
                    ))}
                </div>
                {form.shoot_location === "Other" && (
                    <TextInput value={form.shoot_location_other} onChange={(v) => set({ shoot_location_other: v })} placeholder="Where?" testId="shoot-loc-other" />
                )}
            </div>
            <div>
                <FieldLabel>Preferred time of day</FieldLabel>
                <div className="mt-4 flex flex-wrap gap-2">
                    {SHOOT_TIMES.map((t) => (
                        <Chip key={t} active={form.shoot_time === t} onClick={() => set({ shoot_time: t })} testId={`shoot-time-${t.toLowerCase().replace(/\s+/g, "-")}`}>
                            {t}
                        </Chip>
                    ))}
                </div>
            </div>
            <div>
                <FieldLabel>Photography style</FieldLabel>
                <div className="mt-4 flex flex-wrap gap-2">
                    {PHOTO_STYLES.map((p) => (
                        <Chip key={p} active={form.photography_style === p} onClick={() => set({ photography_style: p })} testId={`photo-style-${p.toLowerCase()}`}>
                            {p}
                        </Chip>
                    ))}
                </div>
            </div>
        </div>
    );
}

function StepTouches({ form, set }) {
    function toggleProp(w) {
        const cur = form.props || [];
        set({ props: cur.includes(w) ? cur.filter((x) => x !== w) : [...cur, w] });
    }
    return (
        <div className="space-y-10" data-testid="story-step-touches">
            <div>
                <FieldLabel sub="We'll style these into your shoot concept.">Anything you'd like included?</FieldLabel>
                <div className="mt-4 flex flex-wrap gap-2">
                    {PROPS.map((p) => (
                        <Chip key={p} active={(form.props || []).includes(p)} onClick={() => toggleProp(p)} testId={`prop-${p.toLowerCase().replace(/\s+/g, "-")}`}>
                            {p}
                        </Chip>
                    ))}
                </div>
            </div>
            <div>
                <FieldLabel>Anything special you'd like us to weave in?</FieldLabel>
                <Textarea value={form.special_request} onChange={(v) => set({ special_request: v })} placeholder="Names of pets, family inside-jokes, song lyrics that mean something — anything." testId="special-request" />
            </div>
        </div>
    );
}

// --------------------------- Validation per step ---------------------------
function isStepValid(stepKey, form) {
    const hasText = (v) => typeof v === "string" && v.trim().length > 0;
    const hasArr = (v) => Array.isArray(v) && v.length > 0;
    switch (stepKey) {
        case "love_story":
            return (
                hasText(form.how_met) &&
                (form.how_met !== "Other" || hasText(form.how_met_other)) &&
                hasText(form.story) &&
                hasText(form.memorable_moment) &&
                hasText(form.bride_loves) &&
                hasText(form.groom_loves)
            );
        case "personality":
            return hasArr(form.vibe_words) && hasText(form.couple_energy);
        case "style":
            return hasArr(form.style_direction) && hasText(form.visual_style);
        case "lifestyle":
            // Section 4: at least one answer
            return (
                hasText(form.favorite_travel) ||
                hasText(form.favorite_movie) ||
                hasText(form.favorite_song) ||
                hasText(form.dream_honeymoon) ||
                hasText(form.favorite_hangout) ||
                hasArr(form.hobbies)
            );
        case "shoot":
            return hasText(form.shoot_location) && hasText(form.shoot_time) && hasText(form.photography_style);
        case "touches":
            return hasArr(form.props) || hasText(form.special_request);
        default:
            return true;
    }
}

// --------------------------- Main component ---------------------------
export default function StoryStudioForm({ analysis, packSize = null, onClose, onSubmitted, onUnlockStart }) {
    const pricing = usePricing();
    const priceLabel = formatINR(pricing.current_paise);
    const [step, setStep] = useState(0);
    const [form, setForm] = useState(() => ({
        how_met: "", how_met_other: "", story: "", memorable_moment: "",
        bride_loves: "", groom_loves: "",
        vibe_words: [], couple_energy: "",
        style_direction: [], visual_style: "",
        favorite_travel: "", favorite_movie: "", favorite_song: "",
        dream_honeymoon: "", favorite_hangout: "", hobbies: [],
        shoot_location: "", shoot_location_other: "", shoot_time: "", photography_style: "",
        props: [], special_request: "",
    }));
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);

    const set = useCallback((patch) => setForm((f) => ({ ...f, ...patch })), []);
    const currentKey = STEPS[step].key;
    const stepValid = useMemo(() => isStepValid(currentKey, form), [currentKey, form]);

    const next = useCallback(() => {
        if (!stepValid) {
            toast.error("Please complete this section before continuing.");
            return;
        }
        if (step < TOTAL - 1) setStep((s) => s + 1);
    }, [step, stepValid]);

    const back = useCallback(() => setStep((s) => Math.max(0, s - 1)), []);

    const submit = useCallback(async () => {
        if (!stepValid) {
            toast.error("Please complete this section before continuing.");
            return;
        }
        setSaving(true);
        try {
            await saveStoryStudio(analysis.id, form);
            setSubmitted(true);
            toast.success("Story saved.");
            onSubmitted?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save your story.");
        } finally {
            setSaving(false);
        }
    }, [analysis?.id, form, stepValid, onSubmitted]);

    function renderStep() {
        switch (currentKey) {
            case "love_story": return <StepLoveStory form={form} set={set} />;
            case "personality": return <StepPersonality form={form} set={set} />;
            case "style": return <StepStyle form={form} set={set} />;
            case "lifestyle": return <StepLifestyle form={form} set={set} />;
            case "shoot": return <StepShoot form={form} set={set} />;
            case "touches": return <StepTouches form={form} set={set} />;
            default: return null;
        }
    }

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/55 backdrop-blur-sm" data-testid="story-studio-modal">
            <div className="min-h-full w-full px-4 py-10 md:py-14">
                <div className="mx-auto max-w-3xl overflow-hidden rounded-[28px] bg-ivory shadow-soft">
                    {/* Header */}
                    <div className="relative border-b border-charcoal/5 bg-white px-7 pt-7 pb-6 md:px-10">
                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="story-studio-close"
                            className="absolute right-5 top-5 inline-flex h-9 w-9 items-center justify-center rounded-full border border-charcoal/10 text-charcoal/60 transition hover:border-charcoal hover:text-charcoal"
                            aria-label="Close"
                        >
                            <X className="h-4 w-4" />
                        </button>
                        <div className="flex items-center gap-2 text-gold">
                            <Sparkles className="h-4 w-4" />
                            <span className="text-[10px] uppercase tracking-[0.28em]">Vowze · Story Studio</span>
                        </div>
                        <h2 className="mt-4 font-serif text-3xl leading-tight text-charcoal md:text-[36px]">
                            {submitted ? "One last step" : "Tell us your story"}
                        </h2>
                        <p className="mt-2 text-[13.5px] text-ink">
                            {submitted
                                ? `Complete the ${priceLabel} payment and your full strategy + Story Studio will unlock.`
                                : "Help us turn your memories into a pre-wedding creative concept, mood board, storyboard and shot list. Takes about 3–5 minutes."}
                        </p>

                        {!submitted && (
                            <div className="mt-6">
                                <div className="flex items-center gap-2">
                                    {STEPS.map((s, i) => (
                                        <div
                                            key={s.key}
                                            className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                                                i <= step ? "bg-gold" : "bg-champagne"
                                            }`}
                                        />
                                    ))}
                                </div>
                                <div className="mt-3 flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-charcoal/55">
                                    <span data-testid="story-studio-step-label">Section {step + 1} of {TOTAL} · {STEPS[step].title}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Body */}
                    <div className="px-7 py-8 md:px-10 md:py-10">
                        {submitted ? (
                            <PaymentGate analysis={analysis} packSize={packSize} onClose={onClose} onUnlockStart={onUnlockStart} />
                        ) : (
                            renderStep()
                        )}
                    </div>

                    {/* Footer */}
                    {!submitted && (
                        <div className="flex flex-col gap-3 border-t border-charcoal/5 bg-white px-7 py-5 md:flex-row md:items-center md:justify-between md:px-10">
                            <button
                                type="button"
                                onClick={back}
                                disabled={step === 0 || saving}
                                data-testid="story-studio-back"
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-charcoal/15 px-5 py-2.5 text-[12.5px] text-charcoal transition hover:border-charcoal disabled:opacity-40"
                            >
                                <ArrowLeft className="h-3.5 w-3.5" /> Back
                            </button>
                            {step < TOTAL - 1 ? (
                                <button
                                    type="button"
                                    onClick={next}
                                    disabled={!stepValid || saving}
                                    data-testid="story-studio-next"
                                    className="inline-flex items-center justify-center gap-2 rounded-full bg-charcoal px-5 py-2.5 text-[12.5px] text-ivory transition hover:bg-charcoal/90 disabled:opacity-50"
                                >
                                    Continue <ArrowRight className="h-3.5 w-3.5" />
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={submit}
                                    disabled={!stepValid || saving}
                                    data-testid="story-studio-submit"
                                    className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-2.5 text-[12.5px] font-medium text-charcoal transition hover:bg-goldDark disabled:opacity-50"
                                >
                                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                                    {saving ? "Saving…" : "Save & continue to payment"}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// --------------------------- Payment gate (post-questionnaire) ---------------------------
import { testUnlock as apiTestUnlock } from "../lib/api";

function DailyLimitBanner({ status }) {
    if (!status) return null;
    const { limit, allocated, remaining, full } = status;
    if (full) {
        return (
            <div
                className="rounded-[16px] bg-amber-50 p-4 text-[12.5px] text-amber-900 ring-1 ring-amber-200"
                data-testid="daily-limit-banner-full"
            >
                <div className="flex items-start gap-2">
                    <CalendarClock className="mt-0.5 h-4 w-4 flex-none text-amber-600" />
                    <div>
                        <div className="font-medium">Today's {limit} report slots are filled.</div>
                        <div className="mt-1 text-amber-800/90">
                            Your payment is still safe to make — it will secure the <span className="font-medium">first slot tomorrow (IST)</span>,
                            and we'll notify you by email the moment generation begins.
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div
            className="rounded-[16px] bg-emerald-50 p-4 text-[12.5px] text-emerald-900 ring-1 ring-emerald-200"
            data-testid="daily-limit-banner-available"
        >
            <div className="flex items-start gap-2">
                <Gauge className="mt-0.5 h-4 w-4 flex-none text-emerald-600" />
                <div>
                    <div className="font-medium">
                        {limit} paid reports available daily · {remaining}/{limit} left today
                    </div>
                    <div className="mt-1 text-emerald-800/90">
                        Vowze delivers a limited number of personalised reports each day to protect quality.
                        If today's {limit} fill up right as you pay, your report simply begins generating
                        first thing tomorrow (IST) — you'll always get your report either way.
                    </div>
                </div>
            </div>
        </div>
    );
}

function PaymentGate({ analysis, packSize, onClose, onUnlockStart }) {
    const pricing = usePricing();
    const [dailyLimitStatus, setDailyLimitStatus] = useState(null);
    useEffect(() => {
        let cancelled = false;
        getDailyLimit().then((d) => { if (!cancelled) setDailyLimitStatus(d); }).catch(() => {});
        return () => { cancelled = true; };
    }, []);
    // If a specific event-pack was picked, price the modal accordingly;
    // fall back to the launch default otherwise.
    const _pkForSize = (Array.isArray(pricing?.event_packs) ? pricing.event_packs : []).find(
        (p) => p && p.events === packSize,
    );
    const priceLabel = formatINR(_pkForSize ? _pkForSize.price_paise : pricing.current_paise);
    const regularLabel = formatINR(pricing.regular_paise);
    const [testOpen, setTestOpen] = useState(false);
    const [testToken, setTestToken] = useState(() => {
        try { return localStorage.getItem("ss_test_token") || ""; }
        catch { return ""; }
    });
    const [testLoading, setTestLoading] = useState(false);
    const [payLoading, setPayLoading] = useState(false);
    // Fresh consent at the paid gate — both required to enable the pay /
    // test-unlock buttons. Legal trail is persisted server-side.
    const [ownershipConsent, setOwnershipConsent] = useState(false);
    const [aiDisclaimerConsent, setAiDisclaimerConsent] = useState(false);
    const consentsOk = ownershipConsent && aiDisclaimerConsent;

    const payNow = useCallback(async () => {
        if (!consentsOk) {
            toast.error("Please accept both consent checkboxes before continuing.");
            return;
        }
        setPayLoading(true);
        try {
            const ok = await loadRazorpayScript();
            if (!ok) {
                toast.error("Could not load Razorpay checkout. Check your network and try again.");
                return;
            }
            const order = await createOrder(analysis.id, packSize);
            if (!order?.key_id) {
                toast.error("Payment gateway not configured yet.");
                return;
            }
            const options = {
                key: order.key_id,
                amount: order.amount,
                currency: order.currency,
                order_id: order.order_id,
                name: "Vowze",
                description: order.pack_size
                    ? `${order.pack_size}-event Vowze pack · ${order.images} visualisations`
                    : "Couple Wedding Full Report",
                theme: { color: "#D6B98C" },
                prefill: {
                    contact: analysis?.phone || "",
                    name: [analysis?.bride_name, analysis?.groom_name].filter(Boolean).join(" & "),
                },
                handler: async (response) => {
                    const toastId = toast.loading("Verifying your payment…");
                    try {
                        const result = await verifyPayment({
                            analysis_id: analysis.id,
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                        });
                        if (result?.paid) {
                            if (result.deferred) {
                                toast.success(
                                    `Payment verified! Today's slots were taken right as you paid — your report is first in line for ${result.report_day_ist} (IST). We'll email you the moment it begins.`,
                                    { id: toastId, duration: 8000 },
                                );
                            } else {
                                toast.success("Payment verified — your full report is generating.", { id: toastId });
                            }
                            onUnlockStart?.();
                            onClose?.();
                        } else {
                            toast.error("Verification returned an unexpected response.", { id: toastId });
                        }
                    } catch (err) {
                        toast.error(err?.response?.data?.detail || "Payment verification failed.", { id: toastId });
                    }
                },
                modal: {
                    ondismiss: () => setPayLoading(false),
                },
            };
            const rzp = new window.Razorpay(options);
            rzp.on("payment.failed", (resp) => {
                toast.error(resp?.error?.description || "Payment failed. Please try again.");
                setPayLoading(false);
            });
            rzp.open();
        } catch (err) {
            toast.error(err?.response?.data?.detail || err?.message || "Could not start payment.");
        } finally {
            setPayLoading(false);
        }
    }, [analysis, consentsOk, onClose, onUnlockStart]);

    const runTestUnlock = useCallback(async () => {
        if (!consentsOk) {
            toast.error("Please accept both consent checkboxes before continuing.");
            return;
        }
        const token = (testToken || "").trim();
        if (!token) {
            toast.error("Enter the test unlock token first.");
            return;
        }
        setTestLoading(true);
        const toastId = toast.loading(`Unlocking… generating full ${priceLabel} wedding strategy (30–90s).`);
        try {
            const res = await apiTestUnlock(analysis.id, token, {
                ownership_consent: ownershipConsent,
                ai_disclaimer_consent: aiDisclaimerConsent,
            });
            try { localStorage.setItem("ss_test_token", token); } catch (_) { /* noop */ }
            if (res?.deferred) {
                toast.success(
                    `Unlock confirmed! Today's slots were taken — your report is first in line for ${res.report_day_ist} (IST).`,
                    { id: toastId, duration: 8000 },
                );
            } else {
                toast.success(
                    res?.paid
                        ? "Already unlocked — loading your full report."
                        : "Unlock started — watch the live progress on your result page.",
                    { id: toastId },
                );
            }
            onUnlockStart?.();
            onClose?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || err?.message || "Test unlock failed.", { id: toastId });
        } finally {
            setTestLoading(false);
        }
    }, [analysis?.id, testToken, onClose, priceLabel, onUnlockStart, consentsOk, ownershipConsent, aiDisclaimerConsent]);

    return (
        <div className="space-y-6" data-testid="story-studio-payment-gate">
            <div className="rounded-[20px] bg-champagne/40 p-5 ring-1 ring-charcoal/5">
                <div className="flex items-center justify-between gap-3">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-gold">Your story is saved</div>
                    <LaunchPill />
                </div>
                <div className="mt-2 text-[14px] text-charcoal">
                    You're one <span className="font-semibold">{priceLabel}</span>
                    {pricing.is_launch && <span className="ml-1 text-charcoal/50 line-through text-[12.5px]">{regularLabel}</span>}
                    {" "}payment away from your full strategy, 5-module report, 10-image AI gallery, and your personalised Story Studio concept.
                </div>
            </div>

            <DailyLimitBanner status={dailyLimitStatus} />

            <ConsentBlock
                ownership={ownershipConsent}
                setOwnership={setOwnershipConsent}
                aiDisclaimer={aiDisclaimerConsent}
                setAiDisclaimer={setAiDisclaimerConsent}
                idPrefix="paid-consent"
            />

            <button
                type="button"
                onClick={payNow}
                disabled={!consentsOk || payLoading}
                data-testid="story-studio-pay-razorpay"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-[14px] font-medium text-charcoal transition hover:bg-goldDark disabled:cursor-not-allowed disabled:opacity-50"
            >
                {payLoading ? (
                    <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Opening secure checkout…
                    </>
                ) : (
                    <>Pay {priceLabel} securely →</>
                )}
            </button>
            <p className="text-center text-[11px] text-charcoal/55">
                UPI · Cards · Netbanking · Wallets — via Razorpay's PCI-DSS secure checkout.
            </p>
            <LegalFooter testId="paid-legal-footer" />

            <div className="border-t border-dashed border-charcoal/10 pt-5">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-charcoal/50">Dev / test access</div>
                        <div className="mt-1 text-[12px] text-ink/80">Bypass payment with a test token.</div>
                    </div>
                    {!testOpen && (
                        <button
                            type="button"
                            onClick={() => setTestOpen(true)}
                            disabled={testLoading}
                            data-testid="story-studio-test-unlock-button"
                            className="inline-flex flex-none items-center justify-center rounded-full border border-charcoal/20 px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-charcoal transition hover:border-charcoal hover:bg-charcoal hover:text-ivory disabled:opacity-60"
                        >
                            Test unlock
                        </button>
                    )}
                </div>
                {testOpen && (
                    <form
                        onSubmit={(e) => { e.preventDefault(); runTestUnlock(); }}
                        className="mt-4 flex flex-col gap-3 sm:flex-row"
                        data-testid="story-studio-test-unlock-form"
                    >
                        <input
                            type="text"
                            value={testToken}
                            onChange={(e) => setTestToken(e.target.value)}
                            placeholder="Test unlock token"
                            autoFocus
                            disabled={testLoading}
                            data-testid="story-studio-test-unlock-input"
                            className="flex-1 rounded-full border border-charcoal/15 bg-ivory px-4 py-2.5 font-mono text-[12px] text-charcoal placeholder:text-charcoal/35 focus:border-charcoal focus:outline-none disabled:opacity-60"
                        />
                        <button
                            type="submit"
                            disabled={testLoading || !testToken.trim() || !consentsOk}
                            data-testid="story-studio-test-unlock-submit"
                            className="inline-flex flex-none items-center justify-center gap-2 rounded-full bg-charcoal px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] text-ivory transition hover:bg-charcoal/90 disabled:opacity-60"
                        >
                            {testLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                            {testLoading ? "Unlocking…" : "Unlock now"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
